import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import api, { resetCSRF } from '../services/api';
import { loadBootstrap, resetBootstrap } from '../services/bootstrapService';
import { getErrorMessage } from '../utils/errorUtils';

// Create context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFetchingUser, setIsFetchingUser] = useState(false);

  // Clear authentication state
  const clearAuthState = () => {
    setCurrentUser(null);
    setError(null);
    setIsFetchingUser(false);
  };

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      // Set a timeout to prevent infinite loading
      const timeoutId = setTimeout(() => {
        setLoading(false);
      }, 10000); // 10 second timeout

      try {
        // Hydrate the session from the consolidated bootstrap endpoint. The
        // merchant and subscription contexts read from the same shared request,
        // collapsing the cold-load waterfall into a single round-trip.
        const data = await loadBootstrap();
        if (data?.user) {
          setCurrentUser(data.user);
          setError(null);
        }
      } catch (error) {
        // 401 simply means there is no active session; leave state cleared.
      } finally {
        clearTimeout(timeoutId);
        setLoading(false);
      }
    };

    initializeAuth();
  }, []); // Only run once on mount

  // Fetch user data
  const fetchUserData = async () => {
    // Prevent multiple simultaneous calls
    if (isFetchingUser) {
      return false;
    }

    try {
      setIsFetchingUser(true);

      // Use the verify endpoint to check session and get user data
      const response = await api.get('/users/auth/verify/');

      if (response.data && response.data.user && response.status === 200) {
        setCurrentUser(response.data.user);
        setError(null); // Clear any previous errors
        return true;
      } else {
        setError('Failed to fetch user data: Invalid response');
        return false;
      }
    } catch (error) {
      // Only clear auth state if it's an authentication error (401)
      if (error.response && error.response.status === 401) {
        setError(error.userMessage || getErrorMessage(error));
        clearAuthState();
      } else {
        setError(error.userMessage || getErrorMessage(error, 'Failed to fetch user data'));
      }

      return false;
    } finally {
      setIsFetchingUser(false);
    }
  };

  // Login function
  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post('/users/auth/login/', {
        email,
        password
      });

      const { user } = response.data;

      // New session - drop any cached bootstrap so dependent contexts refetch.
      resetBootstrap();

      // Set user data - session is handled by cookies
      setCurrentUser(user);
      setError(null);

      return user;
    } catch (error) {
      // Clear auth state on error
      clearAuthState();

      // Set user-friendly error message
      const errorMessage = error.userMessage || getErrorMessage(error, 'Login failed. Please check your credentials and try again.');
      setError(errorMessage);

      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post('/users/auth/register/', userData);

      const { user, session_preserved } = response.data;

      resetBootstrap();

      if (session_preserved) {
        await fetchUserData();
      } else {
        resetCSRF();
        setCurrentUser(user);
      }

      setError(null);

      return { user, sessionPreserved: !!session_preserved };
    } catch (error) {
      const errorMessage = error.userMessage || getErrorMessage(error, 'Registration failed. Please try again.');
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Confirm the current user's email with a 6-digit code
  const verifyEmail = async (email, code) => {
    try {
      setError(null);
      const response = await api.post('/users/auth/verify-email/', { email, code });
      const { user } = response.data;
      setCurrentUser(user);
      return user;
    } catch (error) {
      const errorMessage = error.userMessage || getErrorMessage(error, 'Verification failed. Please try again.');
      setError(errorMessage);
      throw error;
    }
  };

  // Re-send the signup verification code
  const resendVerificationCode = async (email) => {
    try {
      setError(null);
      const response = await api.post('/users/auth/resend-code/', { email });
      return response.data;
    } catch (error) {
      const errorMessage = error.userMessage || getErrorMessage(error, 'Could not resend the code. Please try again.');
      setError(errorMessage);
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await api.post('/users/auth/logout/');
    } catch (error) {
      // Silently fail - user will be logged out anyway
    } finally {
      resetCSRF();
      resetBootstrap();
      clearAuthState();
    }
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!currentUser;
  };

  // Check if user is admin
  const isAdmin = () => {
    return isAuthenticated() && currentUser?.is_staff;
  };

  // Check if user has any merchants
  const hasMerchant = () => {
    return isAuthenticated() && currentUser?.merchant_count > 0;
  };

  // Check if the authenticated user has confirmed their email address
  const isEmailVerified = () => {
    return isAuthenticated() && !!currentUser?.email_verified;
  };

  // Get user's merchants (memoized to prevent unnecessary re-renders)
  const getUserMerchants = useCallback(() => {
    // If we have merchant_ids from the new UserBasicSerializer, use those
    if (currentUser?.merchant_ids && Array.isArray(currentUser.merchant_ids)) {
      // For now, return basic merchant objects with IDs
      // The MerchantContext will fetch full details as needed
      return currentUser.merchant_ids.map(id => ({ id }));
    }

    // Fallback to old merchants array if available (for backward compatibility)
    return currentUser?.merchants || [];
  }, [currentUser?.merchant_ids, currentUser?.merchants]);

  // Get merchant count
  const getMerchantCount = () => {
    return currentUser?.merchant_count || 0;
  };

  // Check if user has access to a specific merchant
  const hasAccessToMerchant = (merchantId) => {
    if (!isAuthenticated() || !currentUser?.merchants) return false;
    return currentUser.merchants.some(merchant => merchant.id === merchantId);
  };

  // Get user's role for a specific merchant
  const getUserRoleForMerchant = (merchantId) => {
    if (!isAuthenticated() || !currentUser?.merchants) return null;
    const merchant = currentUser.merchants.find(m => m.id === merchantId);
    return merchant?.user_role || null;
  };

  const value = {
    currentUser,
    loading,
    error,
    login,
    register,
    verifyEmail,
    resendVerificationCode,
    logout,
    refreshUser: fetchUserData,
    fetchUserData,
    isAuthenticated,
    isAdmin,
    hasMerchant,
    isEmailVerified,
    getUserMerchants,
    getMerchantCount,
    hasAccessToMerchant,
    getUserRoleForMerchant
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
